import { useCallback } from "react";
import { useAuth } from "@clerk/react";
import { CLERK_ENABLED } from "./authStyles";

interface AuthTokenResult {
  readonly getAuthHeaders: () => Promise<Record<string, string>>;
  readonly isSignedIn: boolean;
  /** False until Clerk resolves the session. Consumers that would otherwise fire a throwaway
   *  ANONYMOUS request during the brief pre-resolve window (where isSignedIn reads false) should
   *  wait on this — otherwise a signed-in user does an anon load, then re-loads on auth-upgrade,
   *  producing a visible two-paint (summary lands a beat after moments). When Clerk is disabled
   *  it's always true (there's no session to wait for). */
  readonly isAuthReady: boolean;
}

function useClerkAuthToken(): AuthTokenResult {
  const { getToken, isSignedIn, isLoaded } = useAuth();

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    if (!isSignedIn) return {};
    const token = await getToken({ template: "chess-deck" });
    if (!token) {
      throw new Error("AUTH_TOKEN_UNAVAILABLE");
    }
    return { Authorization: `Bearer ${token}` };
  }, [getToken, isSignedIn]);

  return { getAuthHeaders, isSignedIn: isSignedIn ?? false, isAuthReady: isLoaded ?? false };
}

const noAuthToken: AuthTokenResult = {
  getAuthHeaders: async () => ({}),
  isSignedIn: false,
  isAuthReady: true,
};

export function useAuthToken(): AuthTokenResult {
  const clerkResult = useClerkAuthToken();
  if (!CLERK_ENABLED) return noAuthToken;
  return clerkResult;
}
